import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppVersionTelemetry } from './useAppVersionTelemetry';
import { AuditTelemetryModal } from './AuditTelemetryModal';
import { useSessionRecorder } from './session-recorder';
import { useLabContext } from '../../lab/LabContext';
import { Pin, PinOff, AlertTriangle } from 'lucide-react';
import './audit-telemetry.css';

// Importação lazy para não incluir no bundle caso LAB_MODE esteja desligado
const isLabMode = import.meta.env.DEV || import.meta.env.VITE_LAB_MODE === '1';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let LabFloatingPanel: React.ComponentType<any> | null = null;
if (isLabMode) {
  // Importação estática condicional — compatível com Vite tree-shaking
  import('../../lab/LabFloatingPanel').then((m) => {
    LabFloatingPanel = m.LabFloatingPanel;
  });
}

export const AuditFooterBanner: React.FC = () => {
  const location = useLocation();
  const { isExecuting } = useLabContext();
  const [modalOpen, setModalOpen] = useState(false);
  const [labOpen, setLabOpen] = useState(false);
  const [, forceUpdate] = useState(0); // força re-render após import lazy do Lab
  const labAnchorRef = useRef<HTMLDivElement>(null);

  // Fecha o popup de roteiros quando a execução estiver ativa
  useEffect(() => {
    if (isExecuting) {
      setLabOpen(false);
    }
  }, [isExecuting]);

  const [isPinned, setIsPinned] = useState<boolean>(() => {
    return localStorage.getItem('ac_audit_banner_pinned') === 'true';
  });

  // Ativa a gravação de sessão em background quando habilitada
  useSessionRecorder();

  const telemetry = useAppVersionTelemetry();
  const {
    localManifest,
    remoteManifest,
    isStale,
    isAuditVisible,
    currentRouteMeta,
  } = telemetry;

  const routeCards = currentRouteMeta.relatedTrelloCards || [];
  const routePendenciasCount = routeCards.filter(
    (c) => (c.listName || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes('pendenc')
  ).length;

  // Não renderiza na Landing Page pública
  const isLandingPage =
    location.pathname === '/' ||
    location.pathname.startsWith('/MVP2_Producao_Final_Publicar') ||
    location.pathname.startsWith('/landing');

  // Adiciona classe no body para compensar o padding do sidebar e layout quando visível
  useEffect(() => {
    if (!isLandingPage && isAuditVisible && isPinned) {
      document.body.classList.add('ac-has-audit-banner');
    } else {
      document.body.classList.remove('ac-has-audit-banner');
    }
    return () => {
      document.body.classList.remove('ac-has-audit-banner');
    };
  }, [isLandingPage, isAuditVisible, isPinned]);

  // Força re-render após o módulo do Lab ser carregado
  useEffect(() => {
    if (!isLabMode) return;
    import('../../lab/LabFloatingPanel').then((m) => {
      LabFloatingPanel = m.LabFloatingPanel;
      forceUpdate(n => n + 1);
    });
  }, []);

  const togglePin = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !isPinned;
    setIsPinned(next);
    localStorage.setItem('ac_audit_banner_pinned', String(next));
  };

  const toggleLab = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLabOpen(v => !v);
  };

  if (isLandingPage || !isAuditVisible) {
    return null;
  }

  return (
    <>
      <div className="ac-audit-proximity-container">
        <footer
          className={`ac-audit-banner ${isPinned ? 'ac-audit-banner--pinned' : 'ac-audit-banner--proximity'} ${
            isStale ? 'ac-audit-banner--stale' : ''
          }`}
          onClick={() => setModalOpen(true)}
          title="Clique para abrir a Auditoria de Versão, Trello e Limpeza de Cache"
          role="button"
          tabIndex={0}
        >
          <div className="ac-audit-banner-left">
            <span className="ac-audit-pill ac-audit-pill--tag">{localManifest.version}</span>
            <span className="ac-audit-pill ac-audit-hide-mobile">{localManifest.gitCommit}</span>
            <span className="ac-audit-pill ac-audit-pill--route" title={location.pathname}>
              {location.pathname}
            </span>
            {currentRouteMeta.trelloCard && (
              <span className="ac-audit-pill ac-audit-pill--trello" title={currentRouteMeta.trelloCard}>
                📋 {currentRouteMeta.trelloCard}
              </span>
            )}
            {routePendenciasCount > 0 && (
              <span 
                className="ac-audit-pill bg-amber-400 text-slate-950 font-black border border-amber-500 shadow-xs flex items-center gap-1 animate-pulse"
                title={`PENDÊNCIAS: Esta rota possui ${routePendenciasCount} card(s) no quadro de Pendências`}
              >
                <AlertTriangle size={10} className="text-slate-950" />
                PENDÊNCIAS ({routePendenciasCount})
              </span>
            )}
          </div>

          <div className="ac-audit-banner-right">
            {isStale ? (
              <span className="flex items-center gap-1 text-white font-bold animate-pulse">
                <span className="ac-audit-status-dot ac-audit-status-dot--stale" />
                ⚠️ OBSOLETO (Servidor: {remoteManifest?.version || 'Nova'}) — CLIQUE PARA ATUALIZAR
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-slate-300">
                <span className="ac-audit-status-dot" />
                <span className="ac-audit-hide-mobile">HOMOLOGAÇÃO:</span> SINCRONIZADO
              </span>
            )}

            <button
              onClick={togglePin}
              className={`ac-audit-pin-btn ${isPinned ? 'ac-audit-pin-btn--active' : ''}`}
              title={isPinned ? 'Fixado no Rodapé (Clique para modo Auto-Ocultar por Proximidade)' : 'Modo Proximidade (Clique para Fixar no Rodapé)'}
            >
              {isPinned ? <Pin size={11} /> : <PinOff size={11} />}
            </button>

            {/* ── Botão Lab de Testes (direita, último item) ── */}
            {isLabMode && (
              <div
                ref={labAnchorRef}
                style={{ position: 'relative', flexShrink: 0 }}
                onClick={e => e.stopPropagation()}
              >
                <button
                  onClick={toggleLab}
                  title={labOpen ? 'Fechar Lab de Testes' : 'Abrir Lab de Testes'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '2px 8px',
                    marginLeft: 6,
                    background: labOpen
                      ? 'rgba(59,130,246,0.25)'
                      : 'rgba(255,255,255,0.07)',
                    border: labOpen
                      ? '1px solid rgba(59,130,246,0.5)'
                      : '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 5,
                    color: labOpen ? '#93c5fd' : '#94a3b8',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    letterSpacing: '0.03em',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ fontSize: 12 }}>🧪</span>
                  <span className="ac-audit-hide-mobile">Lab</span>
                </button>

                {/* Popup — âncora no canto direito, abre para cima */}
                {labOpen && LabFloatingPanel && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 8px)',
                      right: 0,
                      zIndex: 10000,
                    }}
                  >
                    <LabFloatingPanel
                      embedded
                      open={labOpen}
                      onClose={() => setLabOpen(false)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </footer>
      </div>

      {/* Modal de Auditoria e Limpeza */}
      <AuditTelemetryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        telemetry={telemetry}
      />
    </>
  );
};
